import { useEffect, useState, useCallback, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton } from "../components/Primitives";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Filter, AlertOctagon, CheckCircle2, Pause, Clock, CheckCircle } from "lucide-react";
import { SortableHeader, useSortableRows } from "../components/SortableTable";
import { fmtDateTime } from "../lib/tz";

const STATUS_META = {
  pending:       { color: "#FF9F0A", label: "PENDING",      Icon: Clock },
  acknowledged:  { color: "#34C759", label: "ACKNOWLEDGED", Icon: CheckCircle2 },
  missed:        { color: "#FF3B30", label: "MISSED",       Icon: AlertOctagon },
  paused:        { color: "#8E8E93", label: "PAUSED",       Icon: Pause },
};

const RESPONSE_META = {
  ok:            { color: "#34C759", label: "All good" },
  investigating: { color: "#FF9F0A", label: "Investigating" },
  need_backup:   { color: "#FF3B30", label: "Backup requested" },
};

const RESOLVED_OPTIONS = [
  { value: "resolved",   label: "Resolved" },
  { value: "unresolved", label: "Unresolved" },
];

/**
 * Read-only admin listing of auto-scheduled status-check prompts.
 * Filter by status + site + guard + resolution + rolling window (days).
 * Sortable by scheduled_at, site, guard, status, response. Resolved missed
 * prompts show a small green check badge next to the status pill.
 */
export default function StatusChecks() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [status, setStatus] = useState("");     // "" = all
  const [siteFilter, setSiteFilter]   = useState([]);   // string[] of site IDs
  const [guardFilter, setGuardFilter] = useState([]);   // string[] of guard IDs
  const [resolvedFilter, setResolvedFilter] = useState([]); // ["resolved"|"unresolved"]

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (status) params.status = status;
      const r = await api.get("/status-checks", { params });
      setRows(r.data || []);
    } catch (e) { toast.error(errorMessage(e, "Load failed")); }
    setLoading(false);
  }, [days, status]);

  useEffect(() => { load(); }, [load]);

  // Join in guard/site names client-side — /status-checks returns raw prompt
  // rows with just IDs. We batch-fetch guards & sites once and index.
  const [siteMap, setSiteMap] = useState({});
  const [siteTzMap, setSiteTzMap] = useState({});
  const [guardMap, setGuardMap] = useState({});
  const [siteOptions, setSiteOptions] = useState([]);
  const [guardOptions, setGuardOptions] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/sites").catch(() => ({ data: [] })),
      api.get("/guards").catch(() => ({ data: [] })),
    ]).then(([s, g]) => {
      const sList = s.data || [];
      const gList = g.data || [];
      setSiteMap(Object.fromEntries(sList.map((x) => [x.id, x.name])));
      setSiteTzMap(Object.fromEntries(sList.map((x) => [x.id, x.timezone || null])));
      setGuardMap(Object.fromEntries(gList.map((x) => [x.id, x.name])));
      setSiteOptions(sList.map((x) => ({ value: x.id, label: x.name })));
      setGuardOptions(gList.map((x) => ({ value: x.id, label: x.name })));
    });
  }, []);

  const decorated = useMemo(() => {
    const siteSet = new Set(siteFilter);
    const guardSet = new Set(guardFilter);
    const resolvedSet = new Set(resolvedFilter);
    return rows
      .filter((r) => (siteSet.size === 0  || siteSet.has(r.site_id))
                  && (guardSet.size === 0 || guardSet.has(r.guard_id))
                  && (resolvedSet.size === 0
                      || (resolvedSet.has("resolved")   &&  r.resolved_at)
                      || (resolvedSet.has("unresolved") && !r.resolved_at)))
      .map((r) => ({
        ...r,
        site_name:  siteMap[r.site_id]   || r.site_id,
        site_timezone: r.site_timezone || siteTzMap[r.site_id] || null,
        guard_name: guardMap[r.guard_id] || r.guard_id,
      }));
  }, [rows, siteMap, siteTzMap, guardMap, siteFilter, guardFilter, resolvedFilter]);

  const accessors = useMemo(() => ({
    scheduled_at: (r) => r.scheduled_at || "",
    site_name:    (r) => (r.site_name || "").toLowerCase(),
    guard_name:   (r) => (r.guard_name || "").toLowerCase(),
    status:       (r) => r.status || "",
    response:     (r) => r.acknowledged_response || "",
  }), []);
  const sortState = useSortableRows(decorated, accessors, {
    defaultKey: "scheduled_at", defaultDir: "desc", storageKey: "status-checks",
  });
  const sorted = sortState.sorted;

  const counts = useMemo(() => {
    const c = { pending: 0, acknowledged: 0, missed: 0, paused: 0 };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <div>
      <PageHeader
        title="Status Checks"
        subtitle="Auto-scheduled welfare acknowledgements from  on shift"
        action={
          <GhostButton onClick={load} disabled={loading} data-testid="status-checks-refresh">
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </GhostButton>
        }
      />

      <div className="p-8 space-y-4">
        {/* Summary pills */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(STATUS_META).map(([key, meta]) => {
            const Icon = meta.Icon;
            return (
              <div
                key={key}
                className="flex items-center gap-2 border border-[#27272A] bg-[#0F0F0F] px-3 py-1.5"
                data-testid={`status-checks-count-${key}`}
              >
                <Icon className="size-3.5" style={{ color: meta.color }} />
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{meta.label}</span>
                <span className="font-mono font-black text-sm" style={{ color: meta.color }}>{counts[key] || 0}</span>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 border border-[#27272A] p-3">
          <div className="flex items-center gap-2">
            <Filter className="size-3.5 text-zinc-500" />
            <span className="label-mute">Filters</span>
          </div>
          <div>
            <div className="label-mute mb-1">Window</div>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1 text-sm outline-none focus:border-[#FF3B30]"
              data-testid="status-checks-days"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <div>
            <div className="label-mute mb-1">Status</div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1 text-sm outline-none focus:border-[#FF3B30]"
              data-testid="status-checks-status-filter"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="missed">Missed</option>
              <option value="paused">Paused (on break)</option>
            </select>
          </div>
          <div>
            <div className="label-mute mb-1">Site</div>
            <MultiSelectDropdown
              testId="status-checks-site-filter"
              label="All sites"
              options={siteOptions}
              selected={siteFilter}
              onChange={setSiteFilter}
              triggerClassName="min-w-[160px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Officer</div>
            <MultiSelectDropdown
              testId="status-checks-guard-filter"
              label="All "
              options={guardOptions}
              selected={guardFilter}
              onChange={setGuardFilter}
              triggerClassName="min-w-[160px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Resolution</div>
            <MultiSelectDropdown
              testId="status-checks-resolved-filter"
              label="Any"
              options={RESOLVED_OPTIONS}
              selected={resolvedFilter}
              onChange={setResolvedFilter}
              triggerClassName="min-w-[140px]"
            />
          </div>
          <div className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 font-mono pb-2" data-testid="status-checks-visible-count">
            {sorted.length} of {rows.length} shown
          </div>
        </div>

        {/* Table */}
        <div className="border border-[#27272A]">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F] border-b border-[#27272A]">
              <tr>
                <SortableHeader k="scheduled_at" state={sortState} className="text-left px-3 py-2">Scheduled</SortableHeader>
                <SortableHeader k="site_name"    state={sortState} className="text-left px-3 py-2">Site</SortableHeader>
                <SortableHeader k="guard_name"   state={sortState} className="text-left px-3 py-2">Officer</SortableHeader>
                <SortableHeader k="status"       state={sortState} className="text-left px-3 py-2">Status</SortableHeader>
                <SortableHeader k="response"     state={sortState} className="text-left px-3 py-2">Response</SortableHeader>
                <th className="text-left px-3 py-2">Ack At</th>
                <th className="text-left px-3 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-500">
                  {loading ? "Loading…" : "No status checks in this window."}
                </td></tr>
              )}
              {sorted.map((r) => {
                const smeta = STATUS_META[r.status] || {};
                const rmeta = RESPONSE_META[r.acknowledged_response] || null;
                return (
                  <tr key={r.id} className="border-b border-[#1F1F1F] hover:bg-[#0F0F0F]" data-testid={`status-check-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-400">
                      {r.scheduled_at ? fmtDateTime(r.scheduled_at, r.site_timezone) : "—"}
                    </td>
                    <td className="px-3 py-2">{r.site_name}</td>
                    <td className="px-3 py-2">{r.guard_name}</td>
                    <td className="px-3 py-2">
                      <PillBadge className="!text-[10px]" style={{ borderColor: smeta.color, color: smeta.color }}>
                        {smeta.label}
                      </PillBadge>
                      {r.status === "missed" && r.resolved_at && (
                        <PillBadge
                          className="!text-[10px] !border-[#34C759] !text-[#34C759] ml-1"
                          title={`Resolved by ${r.resolved_by_name || "supervisor"}${r.resolution_note ? ` — ${r.resolution_note}` : ""}`}
                        >
                          <CheckCircle className="size-3 mr-1" /> RESOLVED
                        </PillBadge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {rmeta ? (
                        <span style={{ color: rmeta.color }} className="text-xs font-mono uppercase tracking-widest">
                          {rmeta.label}
                        </span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-400 font-mono">
                      {r.acknowledged_at ? fmtDateTime(r.acknowledged_at, r.site_timezone) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-400 truncate max-w-[240px]">
                      {r.note || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
